import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useHashRoute, isSpacesHash, COMMUNITY_HISTORY_KEY, currentCommunityEntry } from '@/hooks/useHashRoute'
import type { Screen } from '@/hooks/useHashRoute'
import { Pagination } from '@/components/Pagination'
import { FilterBar } from '@/components/FilterBar'
import { AllFiltersModal } from '@/components/AllFiltersModal'
import { ListControls } from '@/components/ListControls'
import { CardGrid } from '@/components/CardGrid'
import { UnitCard } from '@/components/UnitCard'
import { PlanCard } from '@/components/PlanCard'
import { CommunityCard } from '@/components/CommunityCard'
import { SkeletonCard } from '@/components/SkeletonCard'
import type { SkeletonCardVariant } from '@/components/SkeletonCard'
import { CommunityInfo } from '@/components/CommunityInfo'
import { CommunityMapHeader } from '@/components/CommunityMapHeader'
import { ResultsLayout } from '@/components/ResultsLayout'
import { MapPlaceholder } from '@/components/MapPlaceholder'
import { UnitDetail } from '@/components/UnitDetail'
import { PlanDetail } from '@/components/PlanDetail'
import { Button } from '@/components/Button'
import { LeaseOptionsModal } from '@/components/LeaseOptionsModal'
import { ShareModal } from '@/components/ShareModal'
import { GalleryModal } from '@/components/GalleryModal'
import { VirtualTourModal } from '@/components/VirtualTourModal'
import { NoResultsIllustration } from '@/components/NoResultsIllustration/NoResultsIllustration'
import { CalculatorModal } from '@/components/CalculatorModal'
import { ComponentSandbox } from '@/components/Sandbox'
import { Disclaimer } from '@/components/Disclaimer'
import { PortfolioMap, focusPortfolioMarkerWhenReady, isWithinBounds } from '@/components/PortfolioMap'
import type { MapViewport } from '@/components/PortfolioMap'
import { fetchUnits, fetchPlans, fetchSightmapIds, fetchPortfolioUnits, fetchPortfolioProperties } from '@/api/fetchCollection'
import type { CollectionFilters } from '@/api/fetchCollection'
import { mapUnitToCardProps, mapPlanToCardProps, mapPropertyToCommunityCardProps, formatUnitName, toPhoneHref, resolvePricingDisplay, getUnitProperty } from '@/data/mappers'
import { getOptions, getConfig, getLabels, getPriceBounds, getSortOptions, resolveDefaultSort, resolveDefaultView, resolveDefaultObject, resolveObjectTabs, isPortfolio, getPortfolioConfig, getPortfolioProperty, getCommunityConfig, setActiveAssetConfig, getCustomFilterConfigs, getFilterVisibility } from '@/data/config'
import { parseListState, serializeListState } from '@/utils/urlState'
import type { ListUrlState, ViewMode, CardType } from '@/utils/urlState'
import { leaseDebug } from '@/utils/leaseDebug'
import { decodeEntitiesDeep } from '@/utils/decodeEntities'
import { emitReady, emitUnitsChange, emitPlansChange, emitViewChangeBefore } from '@/metrics/metrics'
import type { ApiCollectionResponse, ApiUnit, ApiFloorPlan, ApiPortfolioPropertiesResponse, LeaseOverride, CustomFilterConfig, SpacesInitialData } from '@/types/api'
import {
	MOCK_UNITS,
	MOCK_PLANS,
	MOCK_GALLERY_IMAGES
} from '@/data/mockData'
import type { FilterOption } from '@/components/FilterContent'

/** Derive bedroom filter options from config (built by build_filter_data on the asset). */
const bedOptions: FilterOption[] = getConfig().filters.bedrooms.map(b => ({
	value: b.cssClasses ? `custom:${b.cssClasses.join('.')}` : String(b.value),
	label: b.label
}))

/** Portfolio mode lists units across a market's properties with a Google map. */
const portfolio = isPortfolio()
const portfolioConfig = getPortfolioConfig()
const EMPTY_PROPERTIES: ApiPortfolioPropertiesResponse = { data: [], total_items: 0 }

const customFilterConfigs: CustomFilterConfig[] = getCustomFilterConfigs()
const filterVisibility = getFilterVisibility()

const priceBounds = getPriceBounds()

/** Convert custom filter config options to FilterOption[] (drop any extra backend fields). */
function toFilterOptions (config: CustomFilterConfig): FilterOption[] {
	return config.options.map(o => ({ value: o.value, label: o.label }))
}

/**
 * Build a complete custom-filter selection map (one entry per configured filter),
 * seeded from an optional parsed subset. Ensures every configured slug exists.
 */
function buildCustomSelections (parsed?: Record<string, string[]>): Record<string, string[]> {
	const selections: Record<string, string[]> = {}
	for (const cf of customFilterConfigs) {
		selections[cf.slug] = parsed?.[cf.slug] ?? []
	}
	return selections
}

/** Numeric fields parsed from a mock unit's display strings. */
function parseMockUnitNumbers (u: (typeof MOCK_UNITS)[number]) {
	return {
		numBeds: u.beds === 'Studio' ? 0 : parseInt(u.beds) || 0,
		numBaths: parseFloat(u.baths) || 1,
		// Parse a dollar string like "$2,050" into a number (2050).
		parsedPrice: u.baseRent ? Number(u.baseRent.replace(/[^0-9.]/g, '')) || null : null,
		leaseTerm: u.leaseTerm ? Number(u.leaseTerm.replace(/\s*Mo\./, '')) : null
	}
}

/** Full mock unit array — kept at module scope for client-side pagination. */
const ALL_MOCK_UNITS: ApiUnit[] = MOCK_UNITS.map((u, i) => {
		const { numBeds, numBaths, parsedPrice, leaseTerm } = parseMockUnitNumbers(u)
		return ({
		id: i,
		unit_number: u.unitNumber,
		plan_name: u.floorPlanName ?? null,
		bedroom_count: numBeds,
		bathroom_count: numBaths,
		area: u.sqft,
		price: parsedPrice,
		price_string: u.baseRent ?? u.priceRange ?? '',
		price_with_fees_string: u.priceRange ?? '',
		price_lease_term: leaseTerm,
		default_lease_term: leaseTerm,
		available_on: '2027-10-16',
		available_on_string: u.availableDate ?? null,
		price_hidden: false,
		available_date_hidden: false,
		is_available: true,
		is_past: false,
		// Raw SightMap floor ID — must never be rendered as a human floor label.
		floor_id: 32788,
		floor_label: `Floor ${i + 1}`,
		floor_plan: {
			id: i,
			name: u.floorPlanName ?? '',
			primary_image_cdn: u.imageUrl ?? null,
			primary_image_alt: null,
			alternate_image_cdn: null,
			alternate_image_caption: null,
			bedroom_count: numBeds,
			bedroom_count_string: u.beds === 'Studio' ? 'Studio' : `${numBeds} Bedroom`,
				bedroom_count_string_short: u.beds,
			bathroom_count: numBaths,
			bathroom_count_string: `${numBaths} Bathroom`,
				bathroom_count_string_short: u.baths,
			area: u.sqft,
			tour_url: null,
				tags: [],
				description: null,
				primary_image_caption: null,
				amenities: [],
				gallery: null
		},
		specials: u.specialMessage ?? null,
		tags: (u.tags ?? []).map(t => ({ name: t.label })),
		unit_tour_url: u.hasVirtualTour ? 'https://tour.example.com' : null,
		is_affordable: false,
			hide_available_date: false,
			unit_fees: 0,
			max_lease_start: null,
			amenities: [],
			gallery: null,
			apply_url: null,
			apply_provider: null,
			hide_apply_link: false,
			on_notice_expiry: 0,
			on_notice_application_date_string: ''
		})
})

const ALL_MOCK_PLANS: ApiFloorPlan[] = MOCK_PLANS.map((p, i) => {
		const numBeds = p.beds === 'Studio' ? 0 : parseInt(p.beds) || 0
		const numBaths = parseFloat(p.baths) || 1
		return ({
			id: i,
			name: p.planName,
			bedroom_count: numBeds,
			bedroom_count_string: p.beds === 'Studio' ? 'Studio' : `${numBeds} Bedroom`,
			bedroom_count_string_short: p.beds,
			bathroom_count: numBaths,
			bathroom_count_string: `${numBaths} Bathroom`,
			bathroom_count_string_short: p.baths,
			area: { min: 0, max: 0, display: p.sqftRange },
			min_price: null,
			min_price_with_required_asset_fees: 0,
			min_price_lease_term: 0,
			starting_at_price: p.baseRent ?? '',
			starting_at_price_with_required_asset_fees: p.totalPrice ?? '',
			starting_at_string: p.totalPrice ?? '',
			primary_image_cdn: p.imageUrl ?? null,
			primary_image_alt: null,
			available_unit_count: p.matchingUnits,
			available_unit_count_label: `${p.matchingUnits} Available`,
			is_available: (p.variant ?? 'default') !== 'unavailable',
			price_hidden: false,
			available_date_hidden: false,
			featured: false,
			tour_url: null,
			tags: []
		})
	})

const MOCK_PER_PAGE = 8

// The properties endpoint returns every community, so the list pages client-side.
const COMMUNITY_PER_PAGE = 8

const LIST_CARD_SELECTOR = '.spaces-results .spaces-unit, .spaces-results .spaces-plan'

/**
 * Build a mock initial data payload from static mock data.
 * Used as a dev-mode fallback when running outside WordPress.
 */
function buildMockInitialData (): SpacesInitialData {
	return {
		units: {
			data: ALL_MOCK_UNITS.slice(0, MOCK_PER_PAGE),
			pagination: { current_page: 1, per_page: MOCK_PER_PAGE, total_items: ALL_MOCK_UNITS.length, total_pages: Math.ceil(ALL_MOCK_UNITS.length / MOCK_PER_PAGE) }
		},
		plans: {
			data: ALL_MOCK_PLANS.slice(0, MOCK_PER_PAGE),
			pagination: { current_page: 1, per_page: MOCK_PER_PAGE, total_items: ALL_MOCK_PLANS.length, total_pages: Math.ceil(ALL_MOCK_PLANS.length / MOCK_PER_PAGE) }
		},
		sightmapUnitIds: ALL_MOCK_UNITS.filter(u => u.is_available).map(u => u.id)
	}
}

/** Allow forcing mock data via ?mock query parameter (used by CSS API tests). */
const forceMock = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('mock')

// Expose mock units and plans on window so E2E tests can patch individual properties.
if (forceMock) {
	;(window as any).__SPACES_MOCK_UNITS = ALL_MOCK_UNITS
	;(window as any).__SPACES_MOCK_PLANS = ALL_MOCK_PLANS
}

/** True when running inside WordPress with server-rendered data. */
const hasLiveData = !forceMock && typeof window !== 'undefined' && !!window.SPACES?.initialData

/** Resolve initial data from WordPress or fall back to mock data for dev. */
const initialData: SpacesInitialData = hasLiveData
	? decodeEntitiesDeep(window.SPACES!.initialData!)
	: buildMockInitialData()

/**
 * Parse the URL hash once at startup to seed list state (filters/sort/tab/view/page)
 * for shareable "Quick Search" links. Validated against config — invalid params drop.
 */
const initialHashQuery = typeof window !== 'undefined' && isSpacesHash(window.location.hash)
	? window.location.hash.replace(/^#/, '').split('?')[1] ?? ''
	: ''
const initialListState = parseListState(initialHashQuery)

/** Price the API sorts and filters on: the TMLP, or base price for data without one. */
const unitSortPrice = (u: ApiUnit) => u.price_tmlp ?? u.price
const planSortPrice = (p: ApiFloorPlan) => p.min_price_tmlp ?? p.min_price

/** The element a host-page anchor hash points at, if any. */
function findAnchorTarget (hash: string): HTMLElement | null {
	const id = hash.replace(/^#/, '')
	if (!id) return null
	try {
		return document.getElementById(decodeURIComponent(id))
	} catch {
		return document.getElementById(id)
	}
}

/** Sort mock units/plans client-side by the given API sort key. */
function sortMockUnits (items: ApiUnit[], sortKey: string): ApiUnit[] {
	const sorted = [...items]
	switch (sortKey) {
		case 'pr_asc': return sorted.sort((a, b) => (unitSortPrice(a) ?? Infinity) - (unitSortPrice(b) ?? Infinity))
		case 'pr_desc': return sorted.sort((a, b) => (unitSortPrice(b) ?? -Infinity) - (unitSortPrice(a) ?? -Infinity))
		case 'sf_asc': return sorted.sort((a, b) => Number(a.area ?? 0) - Number(b.area ?? 0))
		case 'sf_desc': return sorted.sort((a, b) => Number(b.area ?? 0) - Number(a.area ?? 0))
		case 'dt_asc': return sorted.sort((a, b) => (a.available_on ?? '').localeCompare(b.available_on ?? ''))
		case 'dt_desc': return sorted.sort((a, b) => (b.available_on ?? '').localeCompare(a.available_on ?? ''))
		case 'bd_asc': return sorted.sort((a, b) => Number(a.bedroom_count) - Number(b.bedroom_count))
		case 'bd_desc': return sorted.sort((a, b) => Number(b.bedroom_count) - Number(a.bedroom_count))
		default: return sorted
	}
}

function sortMockPlans (items: ApiFloorPlan[], sortKey: string): ApiFloorPlan[] {
	const sorted = [...items]
	switch (sortKey) {
		case 'pr_asc': return sorted.sort((a, b) => (planSortPrice(a) ?? Infinity) - (planSortPrice(b) ?? Infinity))
		case 'pr_desc': return sorted.sort((a, b) => (planSortPrice(b) ?? -Infinity) - (planSortPrice(a) ?? -Infinity))
		case 'sf_asc': return sorted.sort((a, b) => Number(a.area?.min || 0) - Number(b.area?.min || 0))
		case 'sf_desc': return sorted.sort((a, b) => Number(b.area?.min || 0) - Number(a.area?.min || 0))
		case 'bd_asc': return sorted.sort((a, b) => Number(a.bedroom_count) - Number(b.bedroom_count))
		case 'bd_desc': return sorted.sort((a, b) => Number(b.bedroom_count) - Number(a.bedroom_count))
		case 'name_asc': return sorted.sort((a, b) => a.name.localeCompare(b.name))
		default: return sorted
	}
}

/** Convert UI bed string values ("studio", "1", "2") to integer bedroom counts for the API. */
function bedStringsToInts (beds: string[]): number[] {
	return beds
		.filter(b => !b.startsWith('custom:'))
		.map(b => b === 'studio' ? 0 : parseInt(b, 10))
		.filter(n => !isNaN(n))
}

/**
 * Convert selected bed values into CSS class groups for the API.
 * Standard values ("0", "1") become single-class groups (["0bed"], ["1bed"]).
 * Custom values ("custom:2bed.penthouse") become multi-class groups (["2bed", "penthouse"]).
 * Each group is AND'd internally, groups are OR'd with each other.
 */
function bedsToCssClassGroups (beds: string[]): string[][] {
	return beds.map(b => {
		if (b.startsWith('custom:')) {
			return b.slice('custom:'.length).split('.').filter(c => c !== '')
		}
		// Standard bed value — convert to its css_classes equivalent.
		const n = b === 'studio' ? 0 : parseInt(b, 10)
		if (isNaN(n)) return []
		return [`${n}bed`]
	}).filter(group => group.length > 0)
}

/**
 * Build a CollectionFilters object from the current filter state.
 *
 * When only standard bedroom values are selected, uses the `beds` integer
 * filter for efficiency. When any custom value is selected, converts ALL
 * selected values to css_class_groups so the backend can OR them correctly.
 */
function buildFiltersFromState (
	beds: string[],
	priceMin: number | undefined,
	priceMax: number | undefined,
	moveIn: string,
	customSelections?: Record<string, string[]>
): CollectionFilters {
	const filters: CollectionFilters = {}
	const hasCustom = beds.some(b => b.startsWith('custom:'))

	if (beds.length > 0) {
		if (hasCustom) {
			// Mixed or custom-only: convert all values to css_class_groups.
			filters.cssClassGroups = bedsToCssClassGroups(beds)
		} else {
			// Standard-only: use efficient integer filter.
			const bedInts = bedStringsToInts(beds)
			if (bedInts.length > 0) filters.beds = bedInts
		}
	}

	if (priceMin != null) filters.priceMin = priceMin
	if (priceMax != null) filters.priceMax = priceMax
	if (moveIn) filters.availableOn = moveIn

	// Custom CMS-defined filters — include only filters with selections.
	if (customSelections) {
		const active: Record<string, string[]> = {}
		for (const [slug, values] of Object.entries(customSelections)) {
			if (values.length > 0) active[slug] = values
		}
		if (Object.keys(active).length > 0) filters.customFilters = active
	}

	return filters
}

/** Apply filters to mock units client-side. */
function filterMockUnits (items: ApiUnit[], filters: CollectionFilters): ApiUnit[] {
	let result = items
	if (filters.beds && filters.beds.length > 0) {
		result = result.filter(u => filters.beds!.includes(Number(u.bedroom_count)))
	}
	if (filters.cssClassGroups && filters.cssClassGroups.length > 0) {
		result = result.filter(u => {
			if (!u.css_classes) return false
			return filters.cssClassGroups!.some(group =>
				group.every(c => u.css_classes!.includes(c))
			)
		})
	}
	if (filters.priceMin != null) {
		result = result.filter(u => {
			const price = unitSortPrice(u)
			return price != null && price >= filters.priceMin!
		})
	}
	if (filters.priceMax != null) {
		result = result.filter(u => {
			const price = unitSortPrice(u)
			return price != null && price <= filters.priceMax!
		})
	}
	if (filters.availableOn) {
		result = result.filter(u => u.available_on != null && u.available_on <= filters.availableOn!)
	}
	if (filters.customFilters) {
		for (const selected of Object.values(filters.customFilters)) {
			if (selected.length > 0) {
				result = result.filter(u => {
					if (!u.css_classes) return false
					return selected.some(optionValue => {
						const classes = optionValue.split('.').filter(c => c !== '')
						return classes.every(c => u.css_classes!.includes(c))
					})
				})
			}
		}
	}
	return result
}

/** Apply filters to mock plans client-side. */
function filterMockPlans (items: ApiFloorPlan[], filters: CollectionFilters): ApiFloorPlan[] {
	let result = items
	if (filters.beds && filters.beds.length > 0) {
		result = result.filter(p => filters.beds!.includes(Number(p.bedroom_count)))
	}
	if (filters.cssClassGroups && filters.cssClassGroups.length > 0) {
		result = result.filter(p => {
			if (!p.css_classes) return false
			return filters.cssClassGroups!.some(group =>
				group.every(c => p.css_classes!.includes(c))
			)
		})
	}
	if (filters.priceMin != null) {
		result = result.filter(p => {
			const price = planSortPrice(p)
			return price != null && price >= filters.priceMin!
		})
	}
	if (filters.priceMax != null) {
		result = result.filter(p => {
			const price = planSortPrice(p)
			return price != null && price <= filters.priceMax!
		})
	}
	if (filters.customFilters) {
		for (const selected of Object.values(filters.customFilters)) {
			if (selected.length > 0) {
				result = result.filter(p => {
					if (!p.css_classes) return false
					return selected.some(optionValue => {
						const classes = optionValue.split('.').filter(c => c !== '')
						return classes.every(c => p.css_classes!.includes(c))
					})
				})
			}
		}
	}
	return result
}

export function App () {
	const isDesktop = useMediaQuery('(min-width: 880px)')
	const [viewMode, setViewMode] = useState<ViewMode>(() =>
		initialListState.view ?? resolveDefaultView(isDesktop)
	)

	// Override URL for the full map-only view. Populated by the
	// `view.change.before` hook when a theme supplies an alternate embeddable
	// map URL; `null` keeps the default SightMap embed. Passed to MapPlaceholder.
	const [mapOverrideUrl, setMapOverrideUrl] = useState<string | null>(null)

	// Fall back to grid when split view becomes unavailable
	useEffect(() => {
		if (!isDesktop && viewMode === 'split') {
			setViewMode('grid')
		}
	}, [isDesktop, viewMode])

	const [cardType, setCardType] = useState<CardType>(initialListState.tab ?? resolveDefaultObject())
	const { route, navigate, backToList, pushListQuery } = useHashRoute()
	const [sort, setSort] = useState(() => initialListState.sort ?? resolveDefaultSort())

	// API data state — initialised from server-rendered or mock data
	const [unitData, setUnitData] = useState<ApiCollectionResponse<ApiUnit>>(initialData.units)
	const [planData, setPlanData] = useState<ApiCollectionResponse<ApiFloorPlan>>(initialData.plans)
	const [sightmapUnitIds, setSightmapUnitIds] = useState<(string | number)[]>(initialData.sightmapUnitIds)
	const [isLoading, setIsLoading] = useState(false)
	const [loadError, setLoadError] = useState(false)
	const retryRef = useRef<(() => void) | null>(null)
	const listAbortRef = useRef<AbortController | null>(null)

	// Cancels any in-flight list request so a slower, older response can't overwrite newer results.
	const startListRequest = useCallback(() => {
		listAbortRef.current?.abort()
		const controller = new AbortController()
		listAbortRef.current = controller
		return controller
	}, [])

	// Portfolio state — property counts follow the filters; the property filter narrows the unit list.
	const [propertyData, setPropertyData] = useState<ApiPortfolioPropertiesResponse>(initialData.properties ?? EMPTY_PROPERTIES)
	const [propertyFilter, setPropertyFilter] = useState<string | undefined>(initialListState.property)
	const propertyFilterRef = useRef(propertyFilter)
	propertyFilterRef.current = propertyFilter
	// Community view: the portfolio list narrowed to one property, with that property's SightMap.
	const communityProperty = portfolio && propertyFilter ? getPortfolioProperty(propertyFilter) : undefined
	// Unit detail swaps in its unit's property config; community view retargets the page config so the
	// SightMap embed follows its property. Community data requests pass the property explicitly instead.
	if (route.screen !== 'unit-detail') {
		setActiveAssetConfig(route.screen === 'list' && communityProperty ? getCommunityConfig(communityProperty.engrainAssetId) : null)
	}
	const propertyCounts = useMemo(
		() => Object.fromEntries(propertyData.data.map(p => [String(p.engrain_asset_id), p.available_unit_count])),
		[propertyData]
	)
	// Communities with matches, in config order.
	const communityProperties = useMemo(() => {
		if (!portfolioConfig) return []
		const matching = new Set(propertyData.data.map(p => String(p.engrain_asset_id)))
		return portfolioConfig.properties.filter(p => matching.has(p.engrainAssetId))
	}, [propertyData])
	const [propertyPage, setPropertyPage] = useState(() =>
		(initialListState.tab ?? resolveDefaultObject()) === 'properties' ? initialListState.page ?? 1 : 1
	)

	const cardContainerRef = useRef<HTMLDivElement>(null)

	// Scroll position and card to restore when the user returns to the list.
	const listReturnRef = useRef<{ scrollY: number; focusIndex: number } | null>(null)

	/** Open a detail screen from the list, remembering where the user was. */
	const openDetail = useCallback((screen: 'unit-detail' | 'plan-detail', id: string) => {
		const card = document.activeElement?.closest('.spaces-unit, .spaces-plan') ?? null
		const cards = Array.from(document.querySelectorAll(LIST_CARD_SELECTOR))
		listReturnRef.current = { scrollY: window.scrollY, focusIndex: card ? cards.indexOf(card) : -1 }
		navigate(screen, id)
	}, [navigate])

	// When entering a detail view, smoothly scroll the app into view. The app is
	// embedded via shortcode and may sit below the fold; without this the user can
	// be left looking at the bottom of the page when the detail content is shorter
	// than the list they navigated from. Returning to the list restores where the user was.
	const prevScreenRef = useRef<Screen>(route.screen)
	useEffect(() => {
		const prevScreen = prevScreenRef.current
		prevScreenRef.current = route.screen
		if (prevScreen === route.screen) return

		const hash = window.location.hash
		if (!isSpacesHash(hash)) {
			listReturnRef.current = null
			// The screen swap changes the app's height, which shifts anchors below it.
			requestAnimationFrame(() => findAnchorTarget(hash)?.scrollIntoView({ behavior: 'instant', block: 'start' }))
			return
		}

		if (route.screen === 'list') {
			const saved = listReturnRef.current
			listReturnRef.current = null
			if (!saved) return
			requestAnimationFrame(() => {
				window.scrollTo({ top: saved.scrollY, behavior: 'instant' })
				const cards = document.querySelectorAll<HTMLElement>(LIST_CARD_SELECTOR)
				if (saved.focusIndex >= 0 && saved.focusIndex < cards.length) {
					cards[saved.focusIndex].focus({ preventScroll: true })
				}
			})
			return
		}

		if (route.screen !== 'unit-detail' && route.screen !== 'plan-detail') return
		const appEl = document.getElementById('spaces-app')
		if (!appEl) return
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		appEl.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' })
	}, [route.screen])

	// On mobile, split view is unavailable — fall back to grid
	const effectiveViewMode =
		!isDesktop && viewMode === 'split' ? 'grid' : viewMode

	// Portfolio split view: the cards follow the properties inside the map's visible area.
	// The viewport also restores the map camera when it remounts after a community view.
	const [mapViewport, setMapViewport] = useState<MapViewport>()
	const [mapResetSignal, setMapResetSignal] = useState(0)
	// Located properties in view, or null for no restriction. With every located match in view
	// the list is unrestricted, so the refit after a filter change doesn't trigger another fetch.
	const viewportScope = useMemo(() => {
		if (!portfolioConfig || effectiveViewMode !== 'split' || !mapViewport) return null
		const inView = new Set<string>()
		let matchOutOfView = false
		for (const p of portfolioConfig.properties) {
			if (p.lat === null || p.lng === null) continue
			if (isWithinBounds({ lat: p.lat, lng: p.lng }, mapViewport.bounds)) inView.add(p.engrainAssetId)
			else if ((propertyCounts[p.engrainAssetId] ?? 0) > 0) matchOutOfView = true
		}
		return matchOutOfView ? Array.from(inView) : null
	}, [effectiveViewMode, mapViewport, propertyCounts])
	const viewportScopeKey = viewportScope?.join(',') ?? null
	const viewportScopeRef = useRef(viewportScope)
	viewportScopeRef.current = viewportScope
	const viewportEmpty = !communityProperty && !!viewportScope && !viewportScope.some(id => (propertyCounts[id] ?? 0) > 0)

	const scopedCommunityProperties = useMemo(() => {
		if (!viewportScope) return communityProperties
		const inView = new Set(viewportScope)
		return communityProperties.filter(p => inView.has(p.engrainAssetId))
	}, [communityProperties, viewportScope])
	const communityTotalPages = Math.max(1, Math.ceil(scopedCommunityProperties.length / COMMUNITY_PER_PAGE))
	const communityPage = Math.min(propertyPage, communityTotalPages)

	// Derive pagination from the active dataset
	const activeData = cardType === 'units' ? unitData : planData
	const page = cardType === 'properties' ? communityPage : activeData.pagination.current_page
	const totalPages = cardType === 'properties' ? communityTotalPages : activeData.pagination.total_pages
	// An empty map area skips the units request, so the last response doesn't describe it.
	const unitCount = viewportEmpty ? 0 : unitData.pagination.total_items
	const planCount = planData.pagination.total_items

	// Modal state
	const [leaseModalOpen, setLeaseModalOpen] = useState(false)
	const [shareModalOpen, setShareModalOpen] = useState(false)
	const [galleryModalOpen, setGalleryModalOpen] = useState(false)
	const [tourModalOpen, setTourModalOpen] = useState(false)
	const [tourUrl, setTourUrl] = useState<string | undefined>()
	const [tourTitle, setTourTitle] = useState('')
	const [calculatorModalOpen, setCalculatorModalOpen] = useState(false)
	const [calculatorUrl, setCalculatorUrl] = useState('')
	const [allFiltersOpen, setAllFiltersOpen] = useState(false)
	const [galleryImages, setGalleryImages] = useState<Array<{ src: string; caption?: string }>>(MOCK_GALLERY_IMAGES)
	const [galleryIndex, setGalleryIndex] = useState(0)
	const [galleryTitle, setGalleryTitle] = useState('')

	// Lease term override state — persists across pagination and tab switches
	const [leaseOverrides, setLeaseOverrides] = useState<Record<string, LeaseOverride>>({})
	const [activeLeaseUnit, setActiveLeaseUnit] = useState<ApiUnit | null>(null)

	// Filter state (lifted from FilterBar for mobile AllFiltersModal sync)
	const [beds, setBeds] = useState<string[]>(initialListState.beds ?? [])
	const [priceMin, setPriceMin] = useState<number | undefined>(initialListState.priceMin)
	const [priceMax, setPriceMax] = useState<number | undefined>(initialListState.priceMax)
	const [moveIn, setMoveIn] = useState(initialListState.moveIn ?? '')
	const [calendarMonth, setCalendarMonth] = useState(() => new Date().getMonth())
	const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear())

	// Custom filter selections — keyed by filter slug
	const [customFilterSelections, setCustomFilterSelections] = useState<Record<string, string[]>>(
		() => buildCustomSelections(initialListState.customFilters)
	)

	const hasCustomSelections = Object.values(customFilterSelections).some(v => v.length > 0)
	const customFilterActiveCount = Object.values(customFilterSelections).filter(v => v.length > 0).length

	const hasActiveFilters =
		beds.length > 0 ||
		priceMin != null ||
		priceMax != null ||
		moveIn !== '' ||
		hasCustomSelections
	const activeFilterCount = [
		beds.length > 0,
		priceMin != null || priceMax != null,
		moveIn !== ''
	].filter(Boolean).length + customFilterActiveCount

	/** Portfolio: fetch units and properties across the market, plus SightMap IDs for a community view. */
	const fetchPortfolioLists = useCallback(async (
		filterOverrides: CollectionFilters,
		effectiveSort: string,
		unitPage: number,
		property: string | undefined
	) => {
		const communityAsset = property ? getPortfolioProperty(property) : undefined
		const controller = startListRequest()
		setIsLoading(true)
		try {
			const [unitsResult, propertiesResult, ids] = await Promise.all([
				fetchPortfolioUnits(unitPage, undefined, effectiveSort, filterOverrides, property, controller.signal, viewportScopeRef.current ?? undefined),
				fetchPortfolioProperties(filterOverrides, controller.signal),
				communityAsset ? fetchSightmapIds(filterOverrides, communityAsset, controller.signal) : Promise.resolve([])
			])
			if (controller.signal.aborted) return
			setUnitData(unitsResult)
			setPropertyData(propertiesResult)
			setSightmapUnitIds(ids)
			setLoadError(false)
		} catch {
			if (controller.signal.aborted) return
			retryRef.current = () => { void fetchPortfolioLists(filterOverrides, effectiveSort, unitPage, property) }
			setLoadError(true)
		} finally {
			if (listAbortRef.current === controller) setIsLoading(false)
		}
	}, [startListRequest])

	/** Single property: fetch units and plans, reusing the initial SightMap IDs when unfiltered. */
	const fetchPropertyLists = useCallback(async (
		filterOverrides: CollectionFilters,
		effectiveSort: string,
		unitPage: number,
		planPage: number
	) => {
		const controller = startListRequest()
		setIsLoading(true)
		try {
			const hasFilters = Object.keys(filterOverrides).length > 0
			const [unitsResult, plansResult, ids] = await Promise.all([
				fetchUnits(unitPage, undefined, effectiveSort, filterOverrides, controller.signal),
				fetchPlans(planPage, undefined, effectiveSort, filterOverrides, controller.signal),
				hasFilters ? fetchSightmapIds(filterOverrides, undefined, controller.signal) : Promise.resolve(initialData.sightmapUnitIds)
			])
			if (controller.signal.aborted) return
			setUnitData(unitsResult)
			setPlanData(plansResult)
			setSightmapUnitIds(ids)
			setLoadError(false)
		} catch {
			if (controller.signal.aborted) return
			retryRef.current = () => { void fetchPropertyLists(filterOverrides, effectiveSort, unitPage, planPage) }
			setLoadError(true)
		} finally {
			if (listAbortRef.current === controller) setIsLoading(false)
		}
	}, [startListRequest])

	/**
	 * Fetch both unit and plan data for the given filter state.
	 * Accepts explicit values so callers can pass updated state before React re-renders.
	 * Portfolio: `property` null clears the property filter, undefined keeps the current one.
	 */
	const fetchWithFilters = useCallback(async (
		filterOverrides: CollectionFilters,
		sortOverride?: string,
		pageOverrides?: { unitPage?: number; planPage?: number },
		portfolioOptions?: { property?: string | null }
	) => {
		const effectiveSort = sortOverride ?? sort
		const unitPage = pageOverrides?.unitPage ?? 1
		const planPage = pageOverrides?.planPage ?? 1
		if (portfolio && hasLiveData) {
			const property = portfolioOptions?.property === undefined
				? propertyFilterRef.current
				: portfolioOptions.property ?? undefined
			return fetchPortfolioLists(filterOverrides, effectiveSort, unitPage, property)
		}
		if (hasLiveData) return fetchPropertyLists(filterOverrides, effectiveSort, unitPage, planPage)

		const filteredUnits = filterMockUnits(ALL_MOCK_UNITS, filterOverrides)
		const sortedUnits = sortMockUnits(filteredUnits, effectiveSort)
		const unitStart = (unitPage - 1) * MOCK_PER_PAGE
		setUnitData({
			data: sortedUnits.slice(unitStart, unitStart + MOCK_PER_PAGE),
			pagination: { current_page: unitPage, per_page: MOCK_PER_PAGE, total_items: filteredUnits.length, total_pages: Math.ceil(filteredUnits.length / MOCK_PER_PAGE) }
		})
		const filteredPlans = filterMockPlans(ALL_MOCK_PLANS, filterOverrides)
		const sortedPlans = sortMockPlans(filteredPlans, effectiveSort)
		const planStart = (planPage - 1) * MOCK_PER_PAGE
		setPlanData({
			data: sortedPlans.slice(planStart, planStart + MOCK_PER_PAGE),
			pagination: { current_page: planPage, per_page: MOCK_PER_PAGE, total_items: filteredPlans.length, total_pages: Math.ceil(filteredPlans.length / MOCK_PER_PAGE) }
		})
		setSightmapUnitIds(filteredUnits.filter(u => u.is_available).map(u => u.id))
	}, [sort, fetchPortfolioLists, fetchPropertyLists])

	/**
	 * Called when any individual filter popover commits (Save) or when
	 * the AllFiltersModal applies. Because React state isn't updated yet
	 * at the time of the callback, callers pass the new values explicitly.
	 */
	const handleFiltersApply = useCallback((newBeds: string[], newPriceMin: number | undefined, newPriceMax: number | undefined, newMoveIn: string, newSort?: string, newCustomSelections?: Record<string, string[]>) => {
		setBeds(newBeds)
		setPriceMin(newPriceMin)
		setPriceMax(newPriceMax)
		setMoveIn(newMoveIn)
		setPropertyPage(1)
		if (newSort !== undefined) setSort(newSort)
		const effectiveCustom = newCustomSelections ?? customFilterSelections
		if (newCustomSelections) setCustomFilterSelections(newCustomSelections)
		const filters = buildFiltersFromState(newBeds, newPriceMin, newPriceMax, newMoveIn, effectiveCustom)
		void fetchWithFilters(filters, newSort)
	}, [fetchWithFilters, customFilterSelections])

	// Refetch from page 1 when the map's visible area changes which properties the list covers.
	// Community view lists one property regardless, so it only records the new scope.
	const lastViewportScopeKeyRef = useRef(viewportScopeKey)
	useEffect(() => {
		if (lastViewportScopeKeyRef.current === viewportScopeKey) return
		lastViewportScopeKeyRef.current = viewportScopeKey
		if (communityProperty) return
		setPropertyPage(1)
		if (viewportEmpty) return
		void fetchWithFilters(buildFiltersFromState(beds, priceMin, priceMax, moveIn, customFilterSelections))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewportScopeKey])

	const handleClearAllFilters = useCallback(() => {
		setBeds([])
		setPriceMin(undefined)
		setPriceMax(undefined)
		setMoveIn('')
		setPropertyPage(1)
		const cleared: Record<string, string[]> = {}
		for (const cf of customFilterConfigs) cleared[cf.slug] = []
		setCustomFilterSelections(cleared)
		setSightmapUnitIds(initialData.sightmapUnitIds)
		void fetchWithFilters({})
	}, [fetchWithFilters])

	const listUrlDefaults = useCallback(() => ({
		tab: resolveDefaultObject(),
		view: resolveDefaultView(isDesktop),
		sort: resolveDefaultSort()
	}), [isDesktop])

	const listState = useMemo<ListUrlState>(() => ({
		tab: cardType,
		view: viewMode,
		sort,
		beds,
		priceMin,
		priceMax,
		moveIn,
		page,
		customFilters: customFilterSelections,
		property: propertyFilter
	}), [cardType, viewMode, sort, beds, priceMin, priceMax, moveIn, page, customFilterSelections, propertyFilter])

	// Enter and exit must build this identically, or Back to Search pushes instead of going back.
	const allPropertiesQuery = useCallback((tab: CardType) => (
		serializeListState({ ...listState, tab, page: 1, property: undefined }, listUrlDefaults())
	), [listState, listUrlDefaults])

	// Portfolio: open community view in a new history entry so browser Back returns to all properties.
	const enterCommunityView = useCallback((engrainAssetId: string) => {
		if (isLoading) return
		const returnQuery = allPropertiesQuery(cardType)
		const query = serializeListState({ ...listState, tab: 'units', page: 1, property: engrainAssetId }, listUrlDefaults())
		pushListQuery(query, { [COMMUNITY_HISTORY_KEY]: { returnTab: cardType, returnQuery } })
	}, [isLoading, cardType, listState, allPropertiesQuery, listUrlDefaults, pushListQuery])

	// Back to all properties, keeping the current filters and the tab the user came from.
	const exitCommunityView = useCallback(() => {
		const entry = currentCommunityEntry()
		const query = allPropertiesQuery(entry?.returnTab ?? resolveDefaultObject())
		if (entry?.returnQuery === query) history.back()
		else pushListQuery(query)
	}, [allPropertiesQuery, pushListQuery])

	// Community view only lists units.
	useEffect(() => {
		if (communityProperty && cardType !== 'units') setCardType('units')
	}, [communityProperty, cardType])

	// Entering/leaving a community unmounts the focused control, so move focus to the new content.
	const communityHeadingRef = useRef<HTMLHeadingElement>(null)
	const communityId = communityProperty?.engrainAssetId
	const prevCommunityIdRef = useRef(communityId)
	// Community card to focus once skeletons give way to the reloaded list.
	const pendingCommunityFocusRef = useRef<string | null>(null)
	useEffect(() => {
		const prevId = prevCommunityIdRef.current
		prevCommunityIdRef.current = communityId
		pendingCommunityFocusRef.current = null
		if (prevId === communityId || route.screen !== 'list') return
		if (communityId) {
			communityHeadingRef.current?.focus()
			return
		}
		if (!prevId) return
		const card = document.querySelector<HTMLElement>(`.spaces-results .spaces-community[data-property="${CSS.escape(prevId)}"]`)
		if (card) {
			card.focus()
			return
		}
		// Entered from a marker or map view: hold focus on the active tab/view toggle until the marker renders.
		const fallback = document.querySelector<HTMLElement>('.spaces-results .spaces-list-controls .spaces-toggle.spaces-active') ??
			document.querySelector<HTMLElement>('.spaces-view-toggle .spaces-toggle.spaces-active')
		fallback?.focus()
		// The list is showing skeletons, so the card may still be on its way.
		if (isLoading) pendingCommunityFocusRef.current = prevId
		return focusPortfolioMarkerWhenReady(prevId, fallback)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [communityId])

	useEffect(() => {
		const pendingId = pendingCommunityFocusRef.current
		if (isLoading || !pendingId) return
		pendingCommunityFocusRef.current = null
		document.querySelector<HTMLElement>(`.spaces-results .spaces-community[data-property="${CSS.escape(pendingId)}"]`)?.focus()
	}, [isLoading])

	/** Live data: fetch one page of the active list. Portfolio pages only the units list. */
	const fetchListPage = useCallback(async (newPage: number, filters: CollectionFilters) => {
		const controller = startListRequest()
		setIsLoading(true)
		try {
			if (portfolio) {
				if (cardType === 'units') {
					const result = await fetchPortfolioUnits(newPage, undefined, sort, filters, propertyFilter, controller.signal, viewportScopeRef.current ?? undefined)
					if (controller.signal.aborted) return
					setUnitData(result)
				}
			} else if (cardType === 'units') {
				const result = await fetchUnits(newPage, undefined, sort, filters, controller.signal)
				if (controller.signal.aborted) return
				setUnitData(result)
			} else {
				const result = await fetchPlans(newPage, undefined, sort, filters, controller.signal)
				if (controller.signal.aborted) return
				setPlanData(result)
			}
			setLoadError(false)
		} catch {
			if (controller.signal.aborted) return
			retryRef.current = () => { void fetchListPage(newPage, filters) }
			setLoadError(true)
		} finally {
			if (listAbortRef.current === controller) setIsLoading(false)
		}
	}, [cardType, sort, propertyFilter, startListRequest])

	const handlePageChange = useCallback(async (newPage: number) => {
		if (isLoading) return // prevent overlapping fetches

		cardContainerRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start'
		})

		if (cardType === 'properties') {
			setPropertyPage(newPage)
			return
		}

		const filters = buildFiltersFromState(beds, priceMin, priceMax, moveIn, customFilterSelections)

		if (!hasLiveData) {
			// Client-side filter + sort + slice for mock/dev mode
			const start = (newPage - 1) * MOCK_PER_PAGE
			if (cardType === 'units') {
				const filtered = filterMockUnits(ALL_MOCK_UNITS, filters)
				const sorted = sortMockUnits(filtered, sort)
				setUnitData({
					data: sorted.slice(start, start + MOCK_PER_PAGE),
					pagination: { current_page: newPage, per_page: MOCK_PER_PAGE, total_items: filtered.length, total_pages: Math.ceil(filtered.length / MOCK_PER_PAGE) }
				})
			} else {
				const filtered = filterMockPlans(ALL_MOCK_PLANS, filters)
				const sorted = sortMockPlans(filtered, sort)
				setPlanData({
					data: sorted.slice(start, start + MOCK_PER_PAGE),
					pagination: { current_page: newPage, per_page: MOCK_PER_PAGE, total_items: filtered.length, total_pages: Math.ceil(filtered.length / MOCK_PER_PAGE) }
				})
			}
		} else {
			await fetchListPage(newPage, filters)
		}
	}, [cardType, isLoading, sort, beds, priceMin, priceMax, moveIn, customFilterSelections, fetchListPage])

	const handleSortChange = useCallback(async (newSort: string) => {
		if (isLoading) return
		setSort(newSort)

		// Sort applies globally to both the units and plans datasets so that
		// switching tabs keeps the user-selected order (rather than reverting the
		// inactive tab to its default-sorted initial data). fetchWithFilters
		// re-sorts both tabs at page 1 for mock and live modes alike.
		const filters = buildFiltersFromState(beds, priceMin, priceMax, moveIn, customFilterSelections)
		await fetchWithFilters(filters, newSort)
	}, [isLoading, beds, priceMin, priceMax, moveIn, customFilterSelections, fetchWithFilters])

	// User-initiated view-mode change. Fires the public `view.change.before`
	// hook so themes can supply an alternate embeddable map URL for the full
	// map-only view, then applies the new view. The override is captured before
	// the switch so the map's first render in map view already uses it (true "before").
	const handleViewModeChange = useCallback((next: ViewMode) => {
		if (next !== viewMode) {
			const override = emitViewChangeBefore(viewMode, next)
			setMapOverrideUrl(next === 'map' ? override : null)
		}
		setViewMode(next)
	}, [viewMode])

	// Signal metrics consumers that the app is mounted and will emit events.
	useEffect(() => {
		emitReady()
	}, [])

	// On mount, if the URL hash carried filters / a non-default sort / a page > 1,
	// fetch the matching data (server-rendered initialData is always unfiltered page 1).
	useEffect(() => {
		const filters = buildFiltersFromState(beds, priceMin, priceMax, moveIn, customFilterSelections)
		const hasFilters = Object.keys(filters).length > 0
		const nonDefaultSort = sort !== resolveDefaultSort()
		const initialPage = initialListState.page ?? 1
		if (hasFilters || nonDefaultSort || (initialPage > 1 && cardType !== 'properties') || !!initialListState.property) {
			void fetchWithFilters(filters, sort, {
				unitPage: cardType === 'units' ? initialPage : 1,
				planPage: cardType === 'plans' ? initialPage : 1
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Serialize list state into the URL hash so the current view is shareable.
	// Uses replaceState (no history entry / no hashchange) to keep history clean
	// and avoid feeding back into the hashchange sync effect below.
	const lastWrittenHash = useRef<string | null>(null)
	useEffect(() => {
		if (route.screen !== 'list') return
		const query = serializeListState(listState, listUrlDefaults())
		const newHash = query ? `#?${query}` : ''
		if (newHash === lastWrittenHash.current) return
		// Leave a host-page anchor in place while there is no list state to write.
		if (!newHash && !isSpacesHash(window.location.hash)) return
		lastWrittenHash.current = newHash
		history.replaceState(history.state, '', window.location.pathname + window.location.search + newHash)
	}, [route.screen, listState, listUrlDefaults])

	// Apply hash query changes from browser back/forward, an in-page "Quick Search"
	// link, or in-app pushListQuery (enter/exit community). Skips the first run since
	// mount already seeded state. The replaceState writes above never update route.
	const isFirstRouteSync = useRef(true)
	useEffect(() => {
		if (isFirstRouteSync.current) {
			isFirstRouteSync.current = false
			return
		}
		if (route.screen !== 'list') return
		const parsed = parseListState(route.query)
		const newBeds = parsed.beds ?? []
		const newPriceMin = parsed.priceMin
		const newPriceMax = parsed.priceMax
		const newMoveIn = parsed.moveIn ?? ''
		const newSort = parsed.sort ?? resolveDefaultSort()
		const newTab = parsed.tab ?? resolveDefaultObject()
		const newView = parsed.view ?? resolveDefaultView(isDesktop)
		const newCustom = buildCustomSelections(parsed.customFilters)
		const newPage = parsed.page ?? 1
		setBeds(newBeds)
		setPriceMin(newPriceMin)
		setPriceMax(newPriceMax)
		setMoveIn(newMoveIn)
		setSort(newSort)
		setCardType(newTab)
		if (newTab === 'properties') setPropertyPage(newPage)
		// Fire the before-view hook for URL/back-forward-driven changes too, so
		// landing on the map view via a hash link still picks up any override.
		if (newView !== viewMode) {
			const override = emitViewChangeBefore(viewMode, newView)
			setMapOverrideUrl(newView === 'map' ? override : null)
		}
		setViewMode(newView)
		setCustomFilterSelections(newCustom)
		setPropertyFilter(parsed.property)
		const filters = buildFiltersFromState(newBeds, newPriceMin, newPriceMax, newMoveIn, newCustom)
		void fetchWithFilters(filters, newSort, {
			unitPage: newTab === 'units' ? newPage : 1,
			planPage: newTab === 'plans' ? newPage : 1
		}, { property: parsed.property ?? null })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [route.query])

	// Emit a change event for the active tab whenever its current page changes
	// (covers filter, sort, pagination, and tab switches). Matches Spaces 2 by
	// only emitting for the currently selected Units/Plans tab.
	useEffect(() => {
		if (cardType === 'units') {
			emitUnitsChange(unitData.data)
		} else if (cardType === 'plans') {
			emitPlansChange(planData.data)
		}
	}, [cardType, unitData, planData])

	// Plan detail isn't available across portfolio properties.
	useEffect(() => {
		if (portfolio && route.screen === 'plan-detail') navigate('list')
	}, [route.screen, navigate])

	if (route.screen === 'sandbox') {
		return <ComponentSandbox onExit={() => navigate('list')} />
	}

	if (route.screen === 'unit-detail' && route.id) {
		const mockUnit = forceMock
			? ALL_MOCK_UNITS.find(u => String(u.id) === route.id) ?? null
			: undefined
		return (
			<>
				<UnitDetail
					unitId={route.id}
					mockUnit={mockUnit}
					mockUnits={forceMock ? ALL_MOCK_UNITS : undefined}
					leaseOverride={leaseOverrides[route.id]}
					onBack={backToList}
					onUnitClick={(unitId) => navigate('unit-detail', unitId)}
					onPlanClick={portfolio ? undefined : (planId) => navigate('plan-detail', planId)}
					onShare={() => setShareModalOpen(true)}
					onGallery={(images: Array<{ src: string; caption?: string }>, index: number, title?: string) => {
						setGalleryImages(images)
						setGalleryIndex(index)
						setGalleryTitle(title ?? '')
						setGalleryModalOpen(true)
					}}
					onTour={(url: string, title: string) => {
						setTourUrl(url)
						setTourTitle(title)
						setTourModalOpen(true)
					}}
					onLeaseOptions={(u: ApiUnit) => {
						setActiveLeaseUnit(u)
						setLeaseModalOpen(true)
					}}
					onCalculator={(url: string) => {
						setCalculatorUrl(url)
						setCalculatorModalOpen(true)
					}}
				/>
				<LeaseOptionsModal
					open={leaseModalOpen}
					onClose={() => {
						setLeaseModalOpen(false)
						setActiveLeaseUnit(null)
					}}
					unitId={activeLeaseUnit ? String(activeLeaseUnit.id) : ''}
					availableDate={activeLeaseUnit?.available_on ?? null}
					maxLeaseStart={activeLeaseUnit?.max_lease_start ?? null}
					unitFees={activeLeaseUnit?.unit_fees ?? 0}
					basePrice={activeLeaseUnit?.price ?? null}
					initialMoveInDate={activeLeaseUnit ? leaseOverrides[String(activeLeaseUnit.id)]?.moveInDate : undefined}
					initialTerm={activeLeaseUnit
						? (leaseOverrides[String(activeLeaseUnit.id)]?.selectedTerm
							?? (activeLeaseUnit.default_lease_term != null ? Number(activeLeaseUnit.default_lease_term) : undefined))
						: undefined}
					onConfirm={override => {
						leaseDebug('app-apply-override', { unitId: override.unitId, override })
						setLeaseOverrides(prev => ({ ...prev, [override.unitId]: override }))
						setLeaseModalOpen(false)
						setActiveLeaseUnit(null)
					}}
				/>
				<ShareModal
					open={shareModalOpen}
					onClose={() => setShareModalOpen(false)}
				/>
				<GalleryModal
					open={galleryModalOpen}
					onClose={() => setGalleryModalOpen(false)}
					title={galleryTitle}
					images={galleryImages}
					initialIndex={galleryIndex}
				/>
				<VirtualTourModal
					open={tourModalOpen}
					onClose={() => setTourModalOpen(false)}
					title={tourTitle}
					url={tourUrl}
				/>
				<CalculatorModal
					open={calculatorModalOpen}
					onClose={() => setCalculatorModalOpen(false)}
					url={calculatorUrl}
				/>
			</>
		)
	}

	if (route.screen === 'plan-detail' && route.id && !portfolio) {
		const mockPlan = forceMock
			? ALL_MOCK_PLANS.find(p => String(p.id) === route.id) as any ?? null
			: undefined
		return (
			<>
				<PlanDetail
					planId={route.id}
					mockPlan={mockPlan}
					mockUnits={forceMock ? ALL_MOCK_UNITS : undefined}
					onBack={backToList}
					onShare={() => setShareModalOpen(true)}
					onGallery={(images: Array<{ src: string; caption?: string }>, index: number, title?: string) => {
						setGalleryImages(images)
						setGalleryIndex(index)
						setGalleryTitle(title ?? '')
						setGalleryModalOpen(true)
					}}
					onTour={(url: string, title: string) => {
						setTourUrl(url)
						setTourTitle(title)
						setTourModalOpen(true)
					}}
					onCalculator={(url: string) => {
						setCalculatorUrl(url)
						setCalculatorModalOpen(true)
					}}
					onUnitClick={(unitId: string) => navigate('unit-detail', unitId)}
				/>
				<ShareModal
					open={shareModalOpen}
					onClose={() => setShareModalOpen(false)}
				/>
				<GalleryModal
					open={galleryModalOpen}
					onClose={() => setGalleryModalOpen(false)}
					title={galleryTitle}
					images={galleryImages}
					initialIndex={galleryIndex}
				/>
				<VirtualTourModal
					open={tourModalOpen}
					onClose={() => setTourModalOpen(false)}
					title={tourTitle}
					url={tourUrl}
				/>
				<CalculatorModal
					open={calculatorModalOpen}
					onClose={() => setCalculatorModalOpen(false)}
					url={calculatorUrl}
				/>
			</>
		)
	}

	const propertyRows = new Map(propertyData.data.map(p => [String(p.engrain_asset_id), p]))
	const communityStart = (communityPage - 1) * COMMUNITY_PER_PAGE
	const communityCards = cardType === 'properties'
		? scopedCommunityProperties.slice(communityStart, communityStart + COMMUNITY_PER_PAGE).flatMap(property => {
			const row = propertyRows.get(property.engrainAssetId)
			if (!row) return []
			return [
				<CommunityCard
					key={property.engrainAssetId}
					{...mapPropertyToCommunityCardProps(property, row)}
					propertyId={property.engrainAssetId}
					onClick={() => { enterCommunityView(property.engrainAssetId) }}
				/>
			]
		})
		: []

	const paginatedCards = cardType === 'units'
		? unitData.data.map(u => {
			const props = mapUnitToCardProps(u)
			const floorPlan = u.floor_plan
			// Portfolio options are market-wide, so the calculator follows the unit's own property.
			const unitProperty = portfolio ? getUnitProperty(u) : undefined
			const sightmapEmbedUrl = unitProperty ? unitProperty.sightmapEmbedUrl : getConfig().sightmapEmbedUrl
			const calculatorEnabled = (unitProperty ? unitProperty.enableSightmapCalculator : getOptions().enableSightmapCalculator) && !!sightmapEmbedUrl

			// Apply lease override if the user has selected a custom lease term for this unit
			const unitId = String(u.id)
			const override = leaseOverrides[unitId]
			if (override && props.pricing === 'revenue-management') {
				const mode = resolvePricingDisplay(u.pricing_display, u.engrain_asset_id)
				if (mode === 'total' || mode === 'both') {
					// Fees included: the card's primary price is priceRange (hasFees === true).
					props.priceRange = override.priceWithFeesString
					if (mode === 'both') {
						props.baseRent = override.baseRentString
					}
				} else {
					// Base rent only: the card's primary price is baseRent (hasFees === false),
					// so the override must update baseRent — priceRange is not rendered here.
					props.baseRent = override.baseRentString
				}
				props.leaseTerm = `${override.selectedTerm} Mo.`
				leaseDebug('app-render-card', {
					unitId,
					mode,
					priceRange: props.priceRange,
					baseRent: props.baseRent,
					leaseTerm: props.leaseTerm
				})
			}

			return (
				<UnitCard
					key={u.id}
					{...props}
					onClick={() => { openDetail('unit-detail', String(u.id)) }}
					onPricingClick={calculatorEnabled ? () => {
						const override = leaseOverrides[unitId]
						const leaseTerm = override?.selectedTerm?.toString() ?? u.default_lease_term?.toString() ?? ''
						const moveInDate = override?.moveInDate ?? u.available_on ?? ''
						const params = new URLSearchParams()
						params.set('unit_number', u.unit_number)
						params.set('lease_term', leaseTerm)
						params.set('move_in_date', moveInDate)
						params.set('mode', 'expense_calculator')
						setCalculatorUrl(`${sightmapEmbedUrl}?${params.toString()}`)
						setCalculatorModalOpen(true)
					} : undefined}
					onFloorPlanClick={floorPlan && !portfolio ? () => { openDetail('plan-detail', String(floorPlan.id)) } : undefined}
					onTourClick={props.tourUrl ? () => {
						setTourUrl(props.tourUrl)
						setTourTitle(formatUnitName(props.unitNumber))
						setTourModalOpen(true)
					} : undefined}
					onLeaseTermClick={props.pricing === 'revenue-management' && !u.available_date_hidden ? () => {
						leaseDebug('card-lease-term-click', { unitId, currentLeaseTerm: props.leaseTerm })
						setActiveLeaseUnit(u)
						setLeaseModalOpen(true)
					} : undefined}
				/>
			)
		})
		: cardType === 'plans'
			? planData.data.map(p => {
				const props = mapPlanToCardProps(p)
				return (
					<PlanCard
						key={p.id}
						{...props}
						onClick={() => { openDetail('plan-detail', String(p.id)) }}
						onTourClick={props.tourUrl ? () => {
							setTourUrl(props.tourUrl)
							setTourTitle(props.planName)
							setTourModalOpen(true)
						} : undefined}
					/>
				)
			})
			: communityCards

	// One map element serves every view, so its type and position must not
	// depend on the view mode — only its classes and the community header around it do.
	const communityMapCard = effectiveViewMode === 'map' && !!communityProperty
	const showFloorPicker = effectiveViewMode === 'map' && isDesktop
	const mapElement = communityProperty
		? (
			<MapPlaceholder
				key={communityProperty.engrainAssetId}
				asset={communityProperty}
				sightmapUnitIds={sightmapUnitIds}
				onUnitClick={(unitId) => { openDetail('unit-detail', unitId) }}
				mapUrlOverride={mapOverrideUrl}
				framed={!communityMapCard}
				showFloorPicker={showFloorPicker}
				className={communityMapCard ? 'spaces:flex-1' : 'spaces:h-full'}
			/>
			)
		: portfolioConfig
		? (
			<PortfolioMap
				apiKey={portfolioConfig.googleMapsApiKey}
				mapId={portfolioConfig.googleMapId}
				properties={portfolioConfig.properties}
				counts={propertyCounts}
				onPropertyClick={enterCommunityView}
				onViewportChange={setMapViewport}
				initialCamera={mapViewport}
				resetSignal={mapResetSignal}
				className='spaces:h-full'
			/>
			)
		: (
			<MapPlaceholder
				sightmapUnitIds={sightmapUnitIds}
				onUnitClick={(unitId) => { openDetail('unit-detail', unitId) }}
				mockUnits={forceMock ? ALL_MOCK_UNITS : undefined}
				mapUrlOverride={mapOverrideUrl}
				showFloorPicker={showFloorPicker}
				className='spaces:h-full'
			/>
			)

	// Map view has no list controls, so community view wraps its SightMap in a card
	// whose header carries the way back to the portfolio map.
	const mapContent = (
		<div className={communityMapCard ? 'spaces-community-map spaces:flex spaces:flex-col spaces:h-full spaces:bg-canvas-base spaces:rounded-24 spaces:border spaces:border-canvas-3 spaces:overflow-hidden' : 'spaces:h-full'}>
			{communityMapCard && communityProperty && (
				<CommunityMapHeader
					name={communityProperty.name}
					headingRef={communityHeadingRef}
					address={communityProperty.address}
					websiteLink={communityProperty.websiteLink}
					phone={communityProperty.phone ?? undefined}
					phoneHref={toPhoneHref(communityProperty.phone)}
					onBack={exitCommunityView}
					backLabel={isDesktop ? 'Back to Search' : 'Back'}
					stacked={!isDesktop}
				/>
			)}
			{mapElement}
		</div>
	)

	const isSplit = effectiveViewMode === 'split'
	const fillSplitList = !isLoading && isSplit && (!!loadError || viewportEmpty || paginatedCards.length === 0)

	// Keep the current card count while loading so the grid doesn't jump; fall back to a full page.
	const skeletonVariant: Record<CardType, SkeletonCardVariant> = { units: 'unit', plans: 'plan', properties: 'community' }
	const skeletonCount = paginatedCards.length || (cardType === 'properties' ? COMMUNITY_PER_PAGE : activeData.pagination.per_page || MOCK_PER_PAGE)
	// Portfolio calculators vary per property, so only single-property mode reserves the button.
	const skeletonPricingAction = cardType === 'units' && !portfolio && getOptions().enableSightmapCalculator && !!getConfig().sightmapEmbedUrl
	const skeletonCards = Array.from({ length: skeletonCount }, (_, i) => (
		<SkeletonCard key={i} variant={skeletonVariant[cardType]} hasPricingAction={skeletonPricingAction} />
	))

	const communityInfo = communityProperty
		? (
			<CommunityInfo
				name={communityProperty.name}
				headingRef={communityHeadingRef}
				address={communityProperty.address}
				websiteLink={communityProperty.websiteLink}
				phone={communityProperty.phone ?? undefined}
				phoneHref={toPhoneHref(communityProperty.phone)}
				className='spaces:mb-12'
			/>
			)
		: null

	const listControls = (
		<ListControls
			cardType={cardType}
			tabs={resolveObjectTabs()}
			onCardTypeChange={type => {
				setCardType(type)
			}}
			unitCount={unitCount}
			planCount={planCount}
			propertyCount={viewportScope ? scopedCommunityProperties.length : propertyData.total_items}
			propertyTabLabel={getLabels().propertyTabLabel}
			sort={sort}
			onSortChange={handleSortChange}
			hideSort={cardType === 'properties'}
			onBack={communityProperty ? exitCommunityView : undefined}
			backLabel={isDesktop ? 'Back to Search' : 'Back'}
		/>
	)

	return (
		<div className='spaces-app spaces-list-view spaces:flex spaces:flex-col spaces:gap-16 spaces:md:gap-20 spaces:bg-canvas-app spaces:min-h-screen'>
			{/* Sandbox toggle (dev) */}
			{getConfig().debugMode && (
				<div className='spaces:flex spaces:justify-end'>
					<Button
						appearance='ghost'
						color='content3'
						size='xs'
						label='Component Sandbox'
						iconLeft='EyeOpen'
						spacesClass='spaces-sandbox-toggle'
						onClick={() => navigate('sandbox')}
					/>
				</div>
			)}

			{/* FilterBar (includes view mode toggle) */}
			<FilterBar
				viewMode={viewMode}
				onViewModeChange={handleViewModeChange}
				showSplitView={isDesktop}
				beds={beds}
				onBedsChange={setBeds}
				bedOptions={bedOptions}
				showPriceFilter={filterVisibility.price}
				showDateFilter={filterVisibility.date}
				priceMin={priceMin}
				priceMax={priceMax}
				onPriceMinChange={setPriceMin}
				onPriceMaxChange={setPriceMax}
				priceMinBound={priceBounds.min}
				priceMaxBound={priceBounds.max}
				moveIn={moveIn}
				onMoveInChange={setMoveIn}
				calendarMonth={calendarMonth}
				calendarYear={calendarYear}
				onCalendarMonthChange={(m, y) => {
					setCalendarMonth(m)
					setCalendarYear(y)
				}}
				customFilterConfigs={customFilterConfigs}
				customFilterSelections={customFilterSelections}
				onCustomFilterSelectionsChange={setCustomFilterSelections}
				activeFilterCount={activeFilterCount}
				onClearAll={handleClearAllFilters}
				onAllFiltersClick={() => setAllFiltersOpen(true)}
				onFiltersApply={handleFiltersApply}
			/>

			{/* Disclaimer top */}
			<Disclaimer variant='top' text={getLabels().disclaimerTop} />

			{/* Results area */}
			<ResultsLayout
				viewMode={effectiveViewMode}
				listFillHeight={fillSplitList}
				listContent={
					<div
						ref={cardContainerRef}
						aria-busy={isLoading}
						className={`spaces-results spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-24 spaces:overflow-hidden${fillSplitList ? ' spaces:flex spaces:flex-col spaces:h-full' : ''}${isLoading ? ' spaces:pointer-events-none' : ''}`}
					>
						{listControls}
						<div className={`spaces:p-12${fillSplitList ? ' spaces:flex-1 spaces:flex spaces:flex-col' : ''}`}>
							{communityInfo}
							{loadError && !isLoading
								? <LoadError onRetry={() => retryRef.current?.()} className={isSplit ? 'spaces:flex-1' : ''} />
								: viewportEmpty && !isLoading
								? <NoMapAreaResults onResetMap={() => setMapResetSignal(n => n + 1)} className='spaces:flex-1' />
								: paginatedCards.length === 0 && !isLoading
								? <NoResults hasActiveFilters={hasActiveFilters} onClearFilters={handleClearAllFilters} className={isSplit ? 'spaces:flex-1' : ''} />
								: <>
									<CardGrid columns={isSplit ? '2-fixed' : 4}>{isLoading ? skeletonCards : paginatedCards}</CardGrid>
									<Pagination
										currentPage={page}
										totalPages={totalPages}
										onPageChange={handlePageChange}
										className='spaces:pt-12'
									/>
								</>
							}
						</div>
					</div>
				}
				mapContent={mapContent}
			/>

			{/* Disclaimer bottom */}
			<Disclaimer variant='bottom' text={getLabels().disclaimer} link={getLabels().disclaimerLink} />

			{/* Modals */}
			<LeaseOptionsModal
				open={leaseModalOpen}
				onClose={() => {
					setLeaseModalOpen(false)
					setActiveLeaseUnit(null)
				}}
				unitId={activeLeaseUnit ? String(activeLeaseUnit.id) : ''}
				availableDate={activeLeaseUnit?.available_on ?? null}
				maxLeaseStart={activeLeaseUnit?.max_lease_start ?? null}
				unitFees={activeLeaseUnit?.unit_fees ?? 0}
				basePrice={activeLeaseUnit?.price ?? null}
				initialMoveInDate={activeLeaseUnit ? leaseOverrides[String(activeLeaseUnit.id)]?.moveInDate : undefined}
				initialTerm={activeLeaseUnit
					? (leaseOverrides[String(activeLeaseUnit.id)]?.selectedTerm
						?? (activeLeaseUnit.default_lease_term != null ? Number(activeLeaseUnit.default_lease_term) : undefined))
					: undefined}
				onConfirm={override => {
					setLeaseOverrides(prev => ({ ...prev, [override.unitId]: override }))
					setLeaseModalOpen(false)
					setActiveLeaseUnit(null)
				}}
			/>
			<ShareModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} />
			<GalleryModal
				open={galleryModalOpen}
				onClose={() => setGalleryModalOpen(false)}
				title={galleryTitle}
				images={galleryImages}
				initialIndex={galleryIndex}
			/>
			<VirtualTourModal
				open={tourModalOpen}
				onClose={() => setTourModalOpen(false)}
				title={tourTitle}
				url={tourUrl}
			/>
			<CalculatorModal
				open={calculatorModalOpen}
				onClose={() => setCalculatorModalOpen(false)}
				url={calculatorUrl}
			/>
			<AllFiltersModal
				open={allFiltersOpen}
				onClose={() => setAllFiltersOpen(false)}
				sort={sort}
				onSortChange={handleSortChange}
				sortOptions={getSortOptions()}
				beds={beds}
				onBedsChange={setBeds}
				bedOptions={bedOptions}
				showPriceFilter={filterVisibility.price}
				showDateFilter={filterVisibility.date}
				priceMin={priceMin}
				priceMax={priceMax}
				onPriceMinChange={setPriceMin}
				onPriceMaxChange={setPriceMax}
				priceMinBound={priceBounds.min}
				priceMaxBound={priceBounds.max}
				moveIn={moveIn}
				onMoveInChange={setMoveIn}
				calendarMonth={calendarMonth}
				calendarYear={calendarYear}
				onCalendarMonthChange={(m, y) => {
					setCalendarMonth(m)
					setCalendarYear(y)
				}}
				customFilterConfigs={customFilterConfigs}
				customFilterSelections={customFilterSelections}
				onCustomFilterSelectionsChange={setCustomFilterSelections}
				onApply={() => setAllFiltersOpen(false)}
				onFiltersApply={handleFiltersApply}
			/>
		</div>
	)
}

/* ---------- Load Error ---------- */

function LoadError ({ onRetry, className = '' }: { onRetry: () => void; className?: string }) {
	return (
		<div role='alert' className={`spaces-load-error spaces:flex spaces:flex-col spaces:items-center spaces:justify-center spaces:gap-16 spaces:py-80 ${className}`}>
			<NoResultsIllustration />
			<h2 className='spaces:text-18/24 spaces:font-semi spaces:text-content-1'>
				We couldn't load availability.
			</h2>
			<p className='spaces:text-14/24 spaces:font-default spaces:text-content-3'>
				Please check your connection and try again.
			</p>
			<Button
				appearance='solid'
				color='brand'
				size='md'
				spacesClass='spaces-retry'
				onClick={onRetry}
			>
				Retry
			</Button>
		</div>
	)
}

/* ---------- No Results ---------- */

function NoMapAreaResults ({ onResetMap, className = '' }: { onResetMap: () => void; className?: string }) {
	return (
		<div className={`spaces-no-results spaces-no-map-results spaces:flex spaces:flex-col spaces:items-center spaces:justify-center spaces:gap-16 spaces:py-80 spaces:text-center ${className}`}>
			<NoResultsIllustration />
			<h2 className='spaces:text-18/24 spaces:font-semi spaces:text-content-1'>
				No results in this map area.
			</h2>
			<p className='spaces:text-14/24 spaces:font-default spaces:text-content-3'>
				Move or zoom out the map to see more results.
			</p>
			<Button
				appearance='solid'
				color='brand'
				size='md'
				spacesClass='spaces-reset-map'
				onClick={onResetMap}
			>
				Reset Map
			</Button>
		</div>
	)
}

function NoResults ({ hasActiveFilters, onClearFilters, className = '' }: { hasActiveFilters: boolean; onClearFilters: () => void; className?: string }) {
	// No filters applied + empty means the property has no availability (leased up),
	// not that the user filtered results away.
	if (!hasActiveFilters) {
		const { contactLink } = getOptions()
		const { pluralUnitLabel } = getLabels()
		return (
			<div className={`spaces-no-results spaces:flex spaces:flex-col spaces:items-center spaces:justify-center spaces:gap-16 spaces:py-80 spaces:text-center ${className}`}>
				<NoResultsIllustration />
				<h2 className='spaces:text-18 spaces:font-semi spaces:text-content-1'>
					No {pluralUnitLabel} currently available.
				</h2>
				<p className='spaces:text-14 spaces:text-content-3'>
					Reach out to our leasing team if you have any questions.
				</p>
				{contactLink?.url
					? (
						<Button
							appearance='solid'
							color='brand'
							size='md'
							spacesClass='spaces-contact-cta'
							href={contactLink.url}
							target={contactLink.target || undefined}
						>
							{contactLink.title || 'Contact'}
						</Button>
						)
					: (
						<p className='spaces-contact-cta spaces:text-14 spaces:text-content-3'>
							Contact for availability.
						</p>
						)}
			</div>
		)
	}

	const { noResultsHeadline, noResultsBody, noResultsButton } = getLabels()
	return (
		<div className={`spaces-no-results spaces:flex spaces:flex-col spaces:items-center spaces:justify-center spaces:gap-16 spaces:py-80 spaces:text-center ${className}`}>
			<NoResultsIllustration />
			<h2 className='spaces:text-18 spaces:font-semi spaces:text-content-1'>
				{noResultsHeadline}
			</h2>
			<p className='spaces:text-14 spaces:text-content-3'>
				{noResultsBody}
			</p>
			<Button
				appearance='solid'
				color='brand'
				size='md'
				spacesClass='spaces-clear'
				onClick={onClearFilters}
			>
				{noResultsButton}
			</Button>
		</div>
	)
}

